/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";

/**
 * Form for updating a remote_session_issuer. All non-id fields are optional patches.
 */
export type UpdateRemoteSessionIssuerForm = {
  /**
   * Upstream authorization endpoint.
   */
  authorizationEndpoint?: string | undefined;
  /**
   * Advertised grant profiles; metadata evidence is not client authorization or user access.
   */
  authorizationGrantProfilesSupported?: Array<string> | undefined;
  /**
   * Whether the issuer includes the RFC 9207 iss parameter in authorization responses. Omitting the field leaves the stored value unchanged.
   */
  authorizationResponseIssParameterSupported?: boolean | undefined;
  /**
   * Whether the issuer supports OpenID Connect Back-Channel Logout. Omitting the field leaves the stored value unchanged.
   */
  backchannelLogoutSupported?: boolean | undefined;
  /**
   * Claims the issuer can return in ID tokens and from userinfo. Omitting the field leaves the stored value unchanged; an empty array records that the issuer advertises none.
   */
  claimsSupported?: Array<string> | undefined;
  /**
   * Whether the issuer accepts a Client ID Metadata Document URL as client_id (OAuth CIMD draft).
   */
  clientIdMetadataDocumentSupported?: boolean | undefined;
  /**
   * Set or clear the URL of OAuth client setup documentation shown when creating clients. An empty string clears it to NULL; any other value must be an absolute http(s) URL.
   */
  clientSetupDocumentationUrl?: string | undefined;
  /**
   * PKCE code challenge methods advertised by the issuer (RFC 8414 code_challenge_methods_supported). Omitting the field leaves the stored value unchanged; an empty array records that the issuer advertises no methods.
   */
  codeChallengeMethodsSupported?: Array<string> | undefined;
  grantTypesSupported?: Array<string> | undefined;
  /**
   * The remote_session_issuer id.
   */
  id: string;
  /**
   * JWS algorithms the issuer signs ID tokens with. Omitting the field leaves the stored value unchanged; an empty array records that the issuer advertises none.
   */
  idTokenSigningAlgValuesSupported?: Array<string> | undefined;
  /**
   * Set or clear the RFC 7662 token introspection endpoint. An empty string clears it to NULL; any other value must be an absolute https URL, or http on loopback.
   */
  introspectionEndpoint?: string | undefined;
  /**
   * Client authentication methods the introspection endpoint accepts. Omitting the field leaves the stored value unchanged; an empty array records that the issuer advertises none.
   */
  introspectionEndpointAuthMethodsSupported?: Array<string> | undefined;
  /**
   * Issuer URL; matches the iss claim.
   */
  issuer?: string | undefined;
  /**
   * Upstream JWKS URI.
   */
  jwksUri?: string | undefined;
  /**
   * Set or clear the logo asset id. An empty string clears it to NULL; any other value must be a uuid.
   */
  logoAssetId?: string | undefined;
  /**
   * Set or clear the display name. An empty string clears it to NULL.
   */
  name?: string | undefined;
  oidc?: boolean | undefined;
  /**
   * When true, a login that would otherwise request the authorization server's whole scopes_supported omits the scope parameter so the server applies its default. Omit or send null to keep the stored value; false restores the default.
   */
  omitScopeFallback?: boolean | undefined;
  /**
   * Set or clear RFC 8414 op_policy_uri. An empty string clears it to NULL; any other value must be an absolute http(s) URL.
   */
  opPolicyUri?: string | undefined;
  /**
   * Set or clear RFC 8414 op_tos_uri. An empty string clears it to NULL; any other value must be an absolute http(s) URL.
   */
  opTosUri?: string | undefined;
  passthrough?: boolean | undefined;
  /**
   * Upstream RFC 7591 registration endpoint.
   */
  registrationEndpoint?: string | undefined;
  /**
   * Whether the issuer accepts the RFC 8707 resource parameter. Omitting the field leaves the stored value unchanged.
   */
  resourceIndicatorSupported?: boolean | undefined;
  responseTypesSupported?: Array<string> | undefined;
  /**
   * Upstream RFC 7009 revocation endpoint.
   */
  revocationEndpoint?: string | undefined;
  /**
   * Set or clear the operator-pinned scope request. Omitting the field (or sending null) leaves the stored value unchanged; an empty array clears it.
   */
  scopeOverride?: Array<string> | null | undefined;
  scopesSupported?: Array<string> | undefined;
  /**
   * Set or clear RFC 8414 service_documentation. An empty string clears it to NULL; any other value must be an absolute http(s) URL.
   */
  serviceDocumentation?: string | undefined;
  /**
   * Rename the slug.
   */
  slug?: string | undefined;
  /**
   * Upstream token endpoint.
   */
  tokenEndpoint?: string | undefined;
  tokenEndpointAuthMethodsSupported?: Array<string> | undefined;
  /**
   * Set or clear this issuer's MCP tunnel binding. Omission keeps the binding; an empty string clears it; any other value must be a tunneled MCP server in the same project. Platform admins only.
   */
  tunneledMcpServerId?: string | undefined;
  /**
   * Set or clear the OpenID Connect userinfo endpoint. An empty string clears it to NULL; any other value must be an absolute https URL, or http on loopback.
   */
  userinfoEndpoint?: string | undefined;
};

/** @internal */
export type UpdateRemoteSessionIssuerForm$Outbound = {
  authorization_endpoint?: string | undefined;
  authorization_grant_profiles_supported?: Array<string> | undefined;
  authorization_response_iss_parameter_supported?: boolean | undefined;
  backchannel_logout_supported?: boolean | undefined;
  claims_supported?: Array<string> | undefined;
  client_id_metadata_document_supported?: boolean | undefined;
  client_setup_documentation_url?: string | undefined;
  code_challenge_methods_supported?: Array<string> | undefined;
  grant_types_supported?: Array<string> | undefined;
  id: string;
  id_token_signing_alg_values_supported?: Array<string> | undefined;
  introspection_endpoint?: string | undefined;
  introspection_endpoint_auth_methods_supported?: Array<string> | undefined;
  issuer?: string | undefined;
  jwks_uri?: string | undefined;
  logo_asset_id?: string | undefined;
  name?: string | undefined;
  oidc?: boolean | undefined;
  omit_scope_fallback?: boolean | undefined;
  op_policy_uri?: string | undefined;
  op_tos_uri?: string | undefined;
  passthrough?: boolean | undefined;
  registration_endpoint?: string | undefined;
  resource_indicator_supported?: boolean | undefined;
  response_types_supported?: Array<string> | undefined;
  revocation_endpoint?: string | undefined;
  scope_override?: Array<string> | null | undefined;
  scopes_supported?: Array<string> | undefined;
  service_documentation?: string | undefined;
  slug?: string | undefined;
  token_endpoint?: string | undefined;
  token_endpoint_auth_methods_supported?: Array<string> | undefined;
  tunneled_mcp_server_id?: string | undefined;
  userinfo_endpoint?: string | undefined;
};

/** @internal */
export const UpdateRemoteSessionIssuerForm$outboundSchema: z.ZodMiniType<
  UpdateRemoteSessionIssuerForm$Outbound,
  UpdateRemoteSessionIssuerForm
> = z.pipe(
  z.object({
    authorizationEndpoint: z.optional(z.string()),
    authorizationGrantProfilesSupported: z.optional(z.array(z.string())),
    authorizationResponseIssParameterSupported: z.optional(z.boolean()),
    backchannelLogoutSupported: z.optional(z.boolean()),
    claimsSupported: z.optional(z.array(z.string())),
    clientIdMetadataDocumentSupported: z.optional(z.boolean()),
    clientSetupDocumentationUrl: z.optional(z.string()),
    codeChallengeMethodsSupported: z.optional(z.array(z.string())),
    grantTypesSupported: z.optional(z.array(z.string())),
    id: z.string(),
    idTokenSigningAlgValuesSupported: z.optional(z.array(z.string())),
    introspectionEndpoint: z.optional(z.string()),
    introspectionEndpointAuthMethodsSupported: z.optional(z.array(z.string())),
    issuer: z.optional(z.string()),
    jwksUri: z.optional(z.string()),
    logoAssetId: z.optional(z.string()),
    name: z.optional(z.string()),
    oidc: z.optional(z.boolean()),
    omitScopeFallback: z.optional(z.boolean()),
    opPolicyUri: z.optional(z.string()),
    opTosUri: z.optional(z.string()),
    passthrough: z.optional(z.boolean()),
    registrationEndpoint: z.optional(z.string()),
    resourceIndicatorSupported: z.optional(z.boolean()),
    responseTypesSupported: z.optional(z.array(z.string())),
    revocationEndpoint: z.optional(z.string()),
    scopeOverride: z.optional(z.nullable(z.array(z.string()))),
    scopesSupported: z.optional(z.array(z.string())),
    serviceDocumentation: z.optional(z.string()),
    slug: z.optional(z.string()),
    tokenEndpoint: z.optional(z.string()),
    tokenEndpointAuthMethodsSupported: z.optional(z.array(z.string())),
    tunneledMcpServerId: z.optional(z.string()),
    userinfoEndpoint: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      authorizationEndpoint: "authorization_endpoint",
      authorizationGrantProfilesSupported:
        "authorization_grant_profiles_supported",
      authorizationResponseIssParameterSupported:
        "authorization_response_iss_parameter_supported",
      backchannelLogoutSupported: "backchannel_logout_supported",
      claimsSupported: "claims_supported",
      clientIdMetadataDocumentSupported:
        "client_id_metadata_document_supported",
      clientSetupDocumentationUrl: "client_setup_documentation_url",
      codeChallengeMethodsSupported: "code_challenge_methods_supported",
      grantTypesSupported: "grant_types_supported",
      idTokenSigningAlgValuesSupported: "id_token_signing_alg_values_supported",
      introspectionEndpoint: "introspection_endpoint",
      introspectionEndpointAuthMethodsSupported:
        "introspection_endpoint_auth_methods_supported",
      jwksUri: "jwks_uri",
      logoAssetId: "logo_asset_id",
      omitScopeFallback: "omit_scope_fallback",
      opPolicyUri: "op_policy_uri",
      opTosUri: "op_tos_uri",
      registrationEndpoint: "registration_endpoint",
      resourceIndicatorSupported: "resource_indicator_supported",
      responseTypesSupported: "response_types_supported",
      revocationEndpoint: "revocation_endpoint",
      scopeOverride: "scope_override",
      scopesSupported: "scopes_supported",
      serviceDocumentation: "service_documentation",
      tokenEndpoint: "token_endpoint",
      tokenEndpointAuthMethodsSupported:
        "token_endpoint_auth_methods_supported",
      tunneledMcpServerId: "tunneled_mcp_server_id",
      userinfoEndpoint: "userinfo_endpoint",
    });
  }),
);

export function updateRemoteSessionIssuerFormToJSON(
  updateRemoteSessionIssuerForm: UpdateRemoteSessionIssuerForm,
): string {
  return JSON.stringify(
    UpdateRemoteSessionIssuerForm$outboundSchema.parse(
      updateRemoteSessionIssuerForm,
    ),
  );
}
